import Edit from "@mui/icons-material/Edit";
import { Box, Chip, Grid, IconButton, Stack, Tooltip, Typography, useTheme } from "@mui/material";
import { xsHeight, mdHeight } from "components/AppBar";
import { SectionComplianceIssuesButton } from "components/SectionComplianceIssuesButton";
import { tabsOffset } from "components/TabLinks";
import { BILLING_CONTACT_STATES } from "encamp-shared/src/constants/states";
import { FacilityStatus, FeatureFlagOverride, Issue } from "generated-graphql/graphql";
import { useCanEditFacility } from "hooks/useAuthorization";
import { useMemo, useState } from "react";

import { useBasicInformationIssues, useFacility } from "../useFacility";
import { EditBasicsDialog } from "./EditBasicsDialog";
import { V2DisplayOnlyField as DisplayOnlyField } from "./V2DisplayOnlyField";

export const BASICS_SECTION_ID = "basics";
export const BASICS_SECTION_ICON = "🏭";

/** Group issues by their key for per-field display */
function useIssuesByKey(issues: Issue[]) {
  return useMemo(() => {
    const map = new Map<string, Issue[]>();
    for (const issue of issues) {
      if (issue.key) {
        const existing = map.get(issue.key) ?? [];
        existing.push(issue);
        map.set(issue.key, existing);
      }
    }
    return map;
  }, [issues]);
}

export const BasicsSection: React.FC = () => {
  const theme = useTheme();
  const { data } = useFacility();
  const facility = data?.facility;
  const [editOpen, setEditOpen] = useState(false);
  const canEdit = useCanEditFacility();
  const basicsIssues = useBasicInformationIssues();
  const issuesByKey = useIssuesByKey(basicsIssues);

  const enabledPrograms = useMemo(() => {
    const programs: string[] = [];
    if (facility?.chemicalFeatureFlagOverride !== FeatureFlagOverride.Disabled) {
      programs.push("Chemicals");
    }
    if (facility?.wasteFeatureFlagOverride !== FeatureFlagOverride.Disabled) {
      programs.push("Waste");
    }
    return programs;
  }, [facility?.chemicalFeatureFlagOverride, facility?.wasteFeatureFlagOverride]);

  const sortedGovernmentIds = useMemo(() => {
    const ids = facility?.facilityAlternateIds ?? [];
    return [...ids].sort((a, b) => (a.type ?? "").localeCompare(b.type ?? ""));
  }, [facility?.facilityAlternateIds]);

  const tags = useMemo(() => {
    return facility?.tags?.map((t) => t.name) ?? [];
  }, [facility?.tags]);

  const colocatedDisplay = useMemo(() => {
    if (!facility?.colocationReportingFacility) return "No";
    return `Yes — ${facility.colocationReportingFacility.name}`;
  }, [facility?.colocationReportingFacility]);

  // Get issues for FacilityAlternateId model entries
  const altIdIssues = useMemo(
    () => basicsIssues.filter((i: Issue) => i.modelName === "FacilityAlternateId"),
    [basicsIssues]
  );

  return (
    <Box
      id={BASICS_SECTION_ID}
      sx={{
        scrollMarginTop: {
          xs: theme.spacing(xsHeight + tabsOffset + 11),
          md: theme.spacing(mdHeight + tabsOffset + 11),
        },
        "& .edit-icon": { opacity: 0, transition: "opacity 200ms" },
        "&:hover .edit-icon": { opacity: 1 },
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={5}>
        <Typography variant="h6">{BASICS_SECTION_ICON} Basics</Typography>
        <Stack direction="row" alignItems="center" spacing={1}>
          <SectionComplianceIssuesButton
            issues={basicsIssues}
            sectionHeader={`${BASICS_SECTION_ICON} Basics`}
          />
          {canEdit && (
            <Tooltip title="Edit Section">
              <IconButton
                size="small"
                className="edit-icon"
                sx={{ color: "action.active" }}
                onClick={() => setEditOpen(true)}
              >
                <Edit fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      </Stack>

      {editOpen && <EditBasicsDialog onClose={() => setEditOpen(false)} />}

      <Stack spacing={4}>
        <Box>
          <Typography variant="subtitle2" fontWeight={600} mb={3}>
            Basic Information
          </Typography>
          <Grid container columnSpacing={3} rowSpacing={3}>
            <Grid size={12}>
              <DisplayOnlyField
                label="Facility Name"
                text={facility?.name ?? ""}
                issues={issuesByKey.get("name")}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
              <DisplayOnlyField
                label="Facility ID"
                text={facility?.customerFacilityId ?? ""}
                issues={issuesByKey.get("customerFacilityId")}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
              <DisplayOnlyField
                label="Company Name"
                text={facility?.companyName ?? ""}
                issues={issuesByKey.get("companyName")}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
              <DisplayOnlyField
                label="Department"
                text={facility?.department ?? ""}
                issues={issuesByKey.get("department")}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
              <DisplayOnlyField
                label="Business Unit"
                text={facility?.businessUnit ?? ""}
                issues={issuesByKey.get("businessUnit")}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
              <DisplayOnlyField
                label="Phone Number"
                text={facility?.phone ?? ""}
                issues={issuesByKey.get("phone")}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
              <DisplayOnlyField
                label="24-Hour Phone Number"
                text={facility?.phone24Hour ?? ""}
                issues={issuesByKey.get("phone24Hour")}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
              <DisplayOnlyField
                label="Dun & Bradstreet Number"
                text={facility?.dbNumber ?? ""}
                issues={issuesByKey.get("dbNumber")}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
              <DisplayOnlyField
                label="NAICS Code"
                text={facility?.naicsCode?.toString() ?? ""}
                issues={issuesByKey.get("naicsCode")}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
              <DisplayOnlyField
                label="Tags"
                renderValue={() => (
                  <Stack direction="row" flexWrap="wrap" gap={0.5}>
                    {tags.map((tag) => (
                      <Chip key={tag} label={tag} size="small" />
                    ))}
                  </Stack>
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
              <DisplayOnlyField
                label="Enabled Programs"
                renderValue={() => (
                  <Stack direction="row" flexWrap="wrap" gap={0.5}>
                    {enabledPrograms.map((program) => (
                      <Chip key={program} label={program} size="small" />
                    ))}
                  </Stack>
                )}
              />
            </Grid>
          </Grid>
        </Box>

        <Box>
          <Typography variant="subtitle2" fontWeight={600} mb={3}>
            Operational Status
          </Typography>
          <Grid container columnSpacing={3} rowSpacing={3}>
            <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
              <DisplayOnlyField
                label="Facility Type"
                text={facility?.facilityType ?? ""}
                issues={issuesByKey.get("facilityType")}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
              <DisplayOnlyField
                label="Facility Status"
                text={facility?.status ?? ""}
                issues={issuesByKey.get("status")}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
              <DisplayOnlyField
                label="Facility Status Reason"
                text={facility?.statusReason ?? ""}
                issues={issuesByKey.get("statusReason")}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
              <DisplayOnlyField
                label="Date Opened"
                text={facility?.dateOpened ?? ""}
                issues={issuesByKey.get("dateOpened")}
              />
            </Grid>
            {(facility?.status === FacilityStatus.Closed ||
              facility?.status === FacilityStatus.Inactive) && (
              <>
                <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
                  <DisplayOnlyField
                    label="Date Closed"
                    text={facility?.dateClosed ?? ""}
                    issues={issuesByKey.get("dateClosed")}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
                  <DisplayOnlyField
                    label="Date Inactive"
                    text={facility?.dateInactive ?? ""}
                    issues={issuesByKey.get("dateInactive")}
                  />
                </Grid>
              </>
            )}
            <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
              <DisplayOnlyField
                label="Max Occupancy"
                text={
                  facility?.isUnmanned ? "Unmanned" : (facility?.maxNumOccupants?.toString() ?? "")
                }
                issues={issuesByKey.get("maxNumOccupants")}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
              <DisplayOnlyField
                label="Co-located"
                text={colocatedDisplay}
                issues={issuesByKey.get("colocationReportingFacilityId")}
              />
            </Grid>
          </Grid>
        </Box>

        {facility?.state && BILLING_CONTACT_STATES.includes(facility.state) && (
          <Box>
            <Typography variant="subtitle2" fontWeight={600} mb={3}>
              Billing
            </Typography>
            <Grid container columnSpacing={3} rowSpacing={3}>
              <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
                <DisplayOnlyField
                  label="Encamp as Billing Contact"
                  text={facility?.useEncampBillingContact ? "Yes" : "No"}
                  issues={issuesByKey.get("useEncampBillingContact")}
                />
              </Grid>
            </Grid>
          </Box>
        )}

        <Box>
          <Typography variant="subtitle2" fontWeight={600} mb={3}>
            Government IDs
          </Typography>
          {sortedGovernmentIds.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No government IDs.
            </Typography>
          ) : (
            <Grid container columnSpacing={3} rowSpacing={3}>
              {sortedGovernmentIds.map((altId) => (
                <Grid size={{ xs: 12, sm: 6, lg: 4 }} key={altId.id}>
                  <DisplayOnlyField
                    label={altId.type ?? "Government ID"}
                    text={altId.hasNoId ? "No ID" : (altId.value ?? "")}
                    issues={altIdIssues.filter((i) => i.modelId === altId.id)}
                  />
                </Grid>
              ))}
            </Grid>
          )}
        </Box>
      </Stack>
    </Box>
  );
};
